import config from 'config';
import type { Dispatch } from 'redux';
import { differenceInMinutes, format, isSameDay, subDays } from 'date-fns';
import type { UserResponse } from 'src/openapi';
import { UserRole } from 'src/constants';
import HiredIcon from 'src/assets/icons/ApplicantActivityStatusIcons/HiredIcon.svg';
import UnHiredIcon from 'src/assets/icons/ApplicantActivityStatusIcons/UnHired.svg';
import ApplicationSeen from 'src/assets/icons/ApplicantActivityStatusIcons/ApplicationSeen.svg';
import ResumeDownloadIcon from 'src/assets/icons/ApplicantActivityStatusIcons/ResumeDownloadIcon.svg';
import JobConcluded from 'src/assets/icons/ApplicantActivityStatusIcons/AppliedJobTimeline.svg';
import RejectedIcon from 'src/assets/icons/ApplicantActivityStatusIcons/RejectedIcon.svg';
import UnRejectedIcon from 'src/assets/icons/ApplicantActivityStatusIcons/ContactDetailsViewed.svg';
import ShortlistedIcon from 'src/assets/icons/ApplicantActivityStatusIcons/Shortlisted.svg';
import UnShortlistedIcon from 'src/assets/icons/ApplicantActivityStatusIcons/UnShortlisted.svg';
import { setLoginToken } from 'src/redux/slices/auth';
import rememberReturnPath from './returnPath';
import { parseUTCDate } from './utils';

const getUserRole = (userDetails: UserResponse): UserRole => {
  if (userDetails?.user_type === 'Employer') {
    return UserRole.Employer;
  }
  if (userDetails?.user_type === 'PlacementPartner') {
    return UserRole.PlacementPartner;
  }
  if (userDetails?.user_type === 'Faculty') {
    return UserRole.Faculty;
  }
  if (userDetails?.user_type === 'TPO') {
    return UserRole.Tpo;
  }
  if (userDetails.user_type === 'SalesExecutive') {
    return UserRole.Sales;
  }
  if (userDetails.user_type === 'Admin') {
    return UserRole.Admin;
  }
  return UserRole.Applicant;
};

export default getUserRole;

export const matchPublicPaths = (pathname: string) => {
  // For '/job-search' and  '/job-search/:id'
  const pathArray = pathname.split('/').filter((path) => path !== '');
  if (
    (pathArray.length === 1 && pathArray[0] === 'job-search') ||
    (pathArray.length === 2 && pathArray[0] === 'job-search')
  )
    return true;

  return false;
};

export function autoLogin(failedCallback?: Function) {
  // Fetch token from local storage
  const token = JSON.parse(localStorage.getItem('isAdminMode') || 'false')
    ? localStorage.getItem('secondaryToken')
    : localStorage.getItem('loginToken');

  if (token) {
    return (dispatch: Dispatch) => {
      dispatch(setLoginToken(token));
      return Promise.resolve();
    };
  }
  return () => {
    // The other way a session is thrown out of the app: every caller's
    // `failedCallback` replaces the location (with '/' or the page's public
    // twin), and after that nothing says where the user was headed. This fires
    // on any hard load with no usable token: Safari evicting localStorage after
    // seven idle days, a cleared profile, or a second tab after the first one's
    // refresh failed.
    //
    // Except on the routes that are browsable signed-out. `candidateLayout`
    // runs this effect there too, so without the guard every anonymous visitor
    // to /job-search would have their path stored and be sent back to it after
    // a later sign-in, instead of to their dashboard. On those routes there is
    // no session to have been ejected from — a real expiry on /job-search comes
    // through the interceptor's `handleAuthFailure`, which stores it there.
    if (!matchPublicPaths(window.location.pathname)) rememberReturnPath();
    if (failedCallback) failedCallback();
    return Promise.resolve();
  };
}

// Keys that need a sentence; title-casing them would not tell anyone what to do.
const ERROR_COPY: Record<string, string> = {
  content_not_allowed:
    'That looks abusive or inappropriate. Please rephrase it.',
};

export const getFormattedErr = (err: any) =>
  ERROR_COPY[err] ??
  err
    ?.split('_')
    .map((word: any) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ') ??
  'Something went wrong!';

/**
 * Turn an axios failure into something a person can act on.
 *
 * The API answers with two different bodies and only one of them has an `error`
 * key: a ControllerError sends `{success, error}`, while a schema rejection
 * sends `{messages: {<location>: {field: [reason]}}}`. Reading `data.error`
 * alone makes every schema rejection — a missing field, a wrong type, a value
 * past a length cap — arrive as `undefined` and render as "Something went
 * wrong!", even though the response names the field and the reason.
 *
 * The location key is whichever webargs slot the schema bound to, so it is
 * `json` for `@body` and `query` for `@arguments` — and `@arguments` is the
 * larger surface of the two. Walk whatever locations are present rather than
 * hardcoding one.
 *
 * `copy` maps a known error key to a real sentence; anything unmapped falls
 * back to the title-cased key, which still reads like jargon, so add an entry
 * whenever a key becomes user-facing.
 */
const humanizeField = (field: string) =>
  `${field.charAt(0).toUpperCase()}${field.slice(1)}`.replace(/_/g, ' ');

/** First usable "field: reason" inside one webargs location, or undefined. */
const firstFieldMessage = (
  byField: unknown,
  copy?: Record<string, string>,
  parentField?: string,
): string | undefined => {
  if (!byField || typeof byField !== 'object') return undefined;
  const entries: [string, unknown][] = Array.isArray(byField)
    ? byField.map((value, index) => [String(index), value])
    : Object.entries(byField as Record<string, unknown>);
  return entries.reduce<string | undefined>((found, [field, reasons]) => {
    if (found) return found;
    const reason = Array.isArray(reasons)
      ? reasons.find((entry) => typeof entry === 'string' && entry)
      : undefined;
    // A List field keys its errors by index, so `field` is "0" and the name the
    // reader needs is the one a level up.
    const label = /^\d+$/.test(field) && parentField ? parentField : field;
    if (reason) {
      // A @validates_schema error has no field name, so marshmallow files it
      // under `_schema` and the reason is our own error key, not a sentence.
      return field.startsWith('_')
        ? (copy?.[reason] ?? getFormattedErr(reason))
        : `${humanizeField(label)}: ${reason}`;
    }
    // Nested schema: {user_details: {email: ['...']}}, or a List's index map.
    const nested = firstFieldMessage(reasons, copy, field);
    if (nested) return nested;
    return typeof reasons === 'string' && reasons
      ? `${humanizeField(label)}: ${reasons}`
      : undefined;
  }, undefined);
};

export const getApiErrorMessage = (
  err: any,
  copy?: Record<string, string>,
): string => {
  const data = err?.response?.data ?? err;
  const key = data?.error;
  if (typeof key === 'string' && key)
    return copy?.[key] ?? getFormattedErr(key);

  const messages = data?.messages;
  if (messages && typeof messages === 'object') {
    const found = Object.values(messages).reduce<string | undefined>(
      (acc, byField) => acc ?? firstFieldMessage(byField, copy),
      undefined,
    );
    if (found) return found;
  }
  return 'Something went wrong!';
};

export const getAutoLoginRedirectUrl = (token: any) =>
  `${config.LEARNING_AUTO_LOGIN_URL}/login?token=${token}`;

export const difficulties = [
  { value: 0, label: 'Balanced', color: 'rgba(254, 193, 75)' },
  { value: 1, label: 'Easy', color: '#388e3c' },
  { value: 2, label: 'Medium', color: '#ee9d00' },
  { value: 3, label: 'Hard', color: '#db2415' },
];

export function getDifficultyLabel(value: number | undefined) {
  return difficulties.find((difficulty) => difficulty.value === value)?.label;
}
export function getDifficultyColor(value: number | undefined) {
  return difficulties.find((difficulty) => difficulty.value === value)?.color;
}
export const getAvatarByActivityType = (activityType: string) => {
  switch (activityType) {
    case 'ApplicationSeen':
      return ApplicationSeen;
    case 'ShortlistedCandidate':
      return ShortlistedIcon;
    case 'ResumeDownloaded':
      return ResumeDownloadIcon;
    case 'HiredCandidate':
      return HiredIcon;
    case 'UnShortlistedCandidate':
      return UnShortlistedIcon;
    case 'UnHiredCandidate':
      return UnHiredIcon;
    case 'RejectedCandidate':
      return RejectedIcon;
    case 'UnRejectedCandidate':
      return UnRejectedIcon;
    case 'JobConcluded':
      return JobConcluded;
    case 'JobClosed':
      return JobConcluded;
    // ContactDetailsViewed asset — full profile/contact now visible to employer.
    case 'ProfileUnlockedViaSpotlight':
    case 'ProfileUnlockedByEmployer':
      return UnRejectedIcon;
    default:
      return ApplicationSeen;
  }
};

export const formatChatTimestamp = (timestamp: string) => {
  if (!timestamp) return '';
  const messageTime = parseUTCDate(timestamp);
  if (!messageTime) return '';
  const now = new Date();
  const minutesSinceMessage = differenceInMinutes(now, messageTime);
  if (minutesSinceMessage < 60) {
    return minutesSinceMessage ? `${minutesSinceMessage} m` : 'now';
  }
  if (isSameDay(messageTime, now)) {
    return format(messageTime, 'hh:mm a').toUpperCase();
  }
  const yesterday = subDays(now, 1);
  if (isSameDay(messageTime, yesterday)) {
    return 'Yesterday';
  }
  return format(messageTime, 'dd/MM/yy');
};

export const getMultiplayerUserStatus = (
  isHost: boolean,
  isReadyToJoin: boolean,
  status: string,
  isNotCompleted: boolean = true,
) => {
  if (isHost) {
    return 'Host';
  }
  if (status === 'Left' || status === 'Blocked' || status === 'Kicked') {
    return status;
  }
  if (isReadyToJoin) {
    return 'Ready';
  }

  return isNotCompleted ? 'Playing' : 'Pending';
};
